import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';
import { ColumnDataType, ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { LocalDate } from '../../../core/model/local-date';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { InvoiceReminderCommand } from '../../command/invoice-reminder';
import { InvoiceReminder } from '../../model/invoice-reminder';
import { InvoiceReminderCommandHandlerResolver } from '../../service/command/invoice-reminder-command-handler-resolver.service';
import { InvoiceReminderService } from '../../service/data/invoice-reminder.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-invoice-reminder-list',
    templateUrl: './invoice-reminder-list.component.html',
    styleUrls: ['./invoice-reminder-list.component.scss'],
    providers: [InvoiceReminderCommandHandlerResolver],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceReminderListComponent extends MultiselectObjectManagerComponentBase<InvoiceReminder> implements OnInit {
  constructor(
    private readonly _dataService: InvoiceReminderService,
    private readonly _languageService: LanguageService,
    cdf: ColumnDefinitionFactory,
    commandDispatcher: CommandDispatcher,
    commandHandlerResolver: InvoiceReminderCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, commandDispatcher, commandHandlerResolver, messageService, navigationService);

    const defaultOrder = 'Number';
    this.columnDefinitions = [
      cdf.createDateTime('COMMON.CREATED_DATE', 'createdDate'),
      cdf.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      cdf.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipient'),
      cdf.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      cdf.createDate('INVOICE.DUE_DATE', 'invoiceDueDate'),
      cdf.createDate('INVOICE.DEFERMENT_DATE', 'invoiceDefermentDate'),
      cdf.createMoney('INVOICE.INVOICEAMOUNT', 'invoiceTotalAmount'),
      cdf.createMoney('INVOICE.REMAINING_AMOUNT', 'invoiceUnPaidAmount'),
      cdf.createDateTime('COMMON.SENT', 'sentDate', ColumnVisibility.optional),
      cdf.create('INVOICE.DELIVERY_METHOD', 'reminderDeliveryMethodText'),
      cdf.create('COMMON.EMAIL_DELIVERY_STATUS', 'deliveryStatusText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: defaultOrder,
        columnDataType: ColumnDataType.translation,
        helpBuilder: (ir: InvoiceReminder) => ir.emailDeliveryInfo,
      }),
      cdf.createBoolean('INVOICE.REMINDER_GUARANTOR_EXISTS', 'hasGuarantors'),
    ];

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', InvoiceReminderCommand.export)];

    this.addContextMenuItemDefinition('COMMON.OPEN_INVOICE', InvoiceReminderCommand.openInvoice);
    this.addContextMenuItemDefinition('INVOICE.COMMAND.GETPDF', InvoiceReminderCommand.previewReminder);
    this.preferencesKey = 'invoice.reminder-list';
  }

  columnDefinitions: ColumnDefinition[];
  contextMenuDefinition: MenuItemDefinition[];

  hasGuarantors: boolean;

  ngOnInit() {
    let tran = this._languageService.getCurrentTranslations();

    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.defaultFields;
        const companies = definition.companies.map(x => {
          return { caption: x.toString(), value: x.id };
        });
        companies.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

        const fromDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, 1).toISOString(),
          };
        });
        fromDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });

        const toDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, LocalDate.daysInMonth(x.year, x.month)).toISOString(),
          };
        });
        toDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });

        if (
          definition.company === '' &&
          (!definition.fromDate || definition.fromDate === null) &&
          (!definition.toDate || definition.toDate === null) &&
          definition.invoiceRecipient === ''
        )
          if (companies.length >= 2) {
            definition.company = companies[1].value;
          }

        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMMON.CREATED_DATE_FROM',
            'fromDate',
            definition.fromDate,
            fromDates,
            definition.selectedFields.includes('fromDate')
          ),
          new FilterDefinitionItem(
            'COMMON.CREATED_DATE_TO',
            'toDate',
            definition.toDate,
            toDates,
            definition.selectedFields.includes('toDate')
          ),
          new FilterDefinitionItem(
            'INVOICE.RECIPIENT_NAME',
            'invoiceRecipient',
            definition.invoiceRecipient,
            null,
            definition.selectedFields.includes('invoiceRecipient')
          ),
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            companies,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'INVOICE.REMINDER_GUARANTOR_EXISTS',
            'hasGuarantors',
            definition.hasGuarantors,
            [
              { caption: 'COMMON.NOT_SPECIFIED', value: '' },
              { caption: 'YES_TEXT', value: true },
              { caption: 'NO_TEXT', value: false },
            ],
            definition.selectedFields.includes('hasGuarantors')
          ),
        ];
      })
    );
  }
}
